import { colors, COLORS } from '@constants/colors';

// The picker's own styles: the tree that lives in the Scene panel and the wall-layout dialog it
// opens. Both lean on the tree's existing vocabulary (see styles.ts) rather than inventing a
// second look — an "Add Room" branch is a tree branch, and the dialog is the same glass panel the
// script dialogs use.
export const useModuleTreeStyles = () => ({
  // Sits under the group label, one step down in weight, like the catalog category heading it
  // replaces.
  sectionLabel: {
    color: colors.white_40,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding: '8px 0 2px',
  },

  // Indent per level, applied as padding rather than a margin so the whole row stays clickable
  // out to the panel edge.
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minHeight: '30px',
    padding: '4px 8px',
    borderRadius: '8px',
    cursor: 'pointer',
    color: colors.white_60,
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: COLORS.WHITE,
    },
  },
  // A branch reads as structure, a leaf as an action: the leaf is the row that puts a room in the
  // layout, so it is the one that brightens and carries the add icon.
  rowLeaf: {
    color: colors.white_70,
  },
  // Catalogued with no room level built yet. Dimmed rather than disabled — UE greyboxes it and the
  // author can still place it.
  rowUnbuilt: {
    opacity: 0.55,
  },
  rowChevron: {
    fontSize: 14,
    color: colors.white_40,
    transition: 'transform 0.15s ease',
    flexShrink: 0,
  },
  rowChevronOpen: {
    transform: 'rotate(90deg)',
  },
  // Keeps a leaf's label aligned with a branch's, which spends that space on its chevron.
  rowLeafSpacer: {
    width: '14px',
    flexShrink: 0,
  },
  rowIcon: {
    fontSize: 15,
    color: colors.white_40,
    flexShrink: 0,
  },
  rowLabel: {
    flex: 1,
    fontSize: '13px',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // How many modules a branch holds, so a collapsed branch still says how much is behind it.
  rowCount: {
    color: colors.white_40,
    fontSize: '11px',
    fontWeight: 500,
    flexShrink: 0,
  },
  rowAddIcon: {
    fontSize: 15,
    color: colors.white_40,
    flexShrink: 0,
  },
  rowWarning: {
    fontSize: 14,
    color: '#FFB800',
    flexShrink: 0,
  },
  rowWarningSevere: {
    color: '#FF6B6B',
  },
  empty: {
    color: colors.white_40,
    fontSize: '12px',
    lineHeight: 1.4,
    padding: '6px 8px',
  },
});

// Matches the script dialogs: same glass gradient, blur, hairline border and radius, so the
// wall-layout step reads as part of the editor rather than as a stock MUI dialog.
export const wallLayoutDialogPaperSx = {
  background:
    'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
  borderRadius: '16px',
  color: '#e2e4ed',
  width: '100%',
  // Three cards, their gaps and the two arrows. A kit can ship more than three wall layouts, and
  // the rest are reached by scrolling the track rather than by growing the dialog — see `track`.
  maxWidth: '860px',
};

// How many cards the track shows at once. The dialog is sized to exactly this many, so anything
// beyond it is scrollable by construction and the arrows have somewhere to go.
export const WALL_LAYOUT_VISIBLE_CARDS = 3;
const CARD_WIDTH_PX = 220;
const CARD_GAP_PX = 12;

export const WALL_LAYOUT_CARD_STEP_PX = CARD_WIDTH_PX + CARD_GAP_PX;

export const useWallLayoutStyles = () => ({
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: 600,
    padding: '18px 20px 6px',
  },
  titleText: {
    flex: 1,
    fontSize: '16px',
    fontWeight: 600,
  },
  // Which module the variants belong to, so the question is not floating free of the room it is
  // about.
  subtitle: {
    color: colors.white_50,
    fontSize: '12px',
    padding: '0 20px 12px',
  },
  content: {
    padding: '0 20px 4px',
  },
  // The track and the arrows that move it. The arrows sit outside the track rather than over the
  // cards, so they never cover a preview an author is trying to compare.
  carousel: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  // One row that scrolls sideways. Native overflow rather than a transform: the wheel, a touchpad
  // swipe and the keyboard all work without this component implementing any of them, and the
  // browser clamps at both ends so the arrows cannot run off.
  track: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: `${CARD_GAP_PX}px`,
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    // Cards land on a card boundary rather than halfway across one.
    scrollSnapType: 'x mandatory',
    // Room for the scrollbar, so it does not sit on the last card's border.
    paddingBottom: '8px',
    '&::-webkit-scrollbar': { height: '6px' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(255, 255, 255, 0.14)',
      borderRadius: '3px',
    },
  },
  arrow: {
    color: colors.white_50,
    flexShrink: 0,
    '&:hover': {
      color: COLORS.WHITE,
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    // Fixed rather than flexible: three of them are exactly what the dialog is sized for, and a
    // card that grew to fill the row would make the fourth invisible instead of scrollable.
    flex: `0 0 ${CARD_WIDTH_PX}px`,
    scrollSnapAlign: 'start',
    padding: '10px',
    textAlign: 'left',
    cursor: 'pointer',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: 'inherit',
    font: 'inherit',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      borderColor: 'rgba(255, 255, 255, 0.22)',
    },
  },
  // Fixed 16:9 box whether or not there is art, so the cards line up and the row does not reflow
  // when thumbnails start arriving.
  preview: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  // The kit ships no thumbnails yet. A muted icon and a line of text say so, rather than a broken
  // image frame or a stretched placeholder pretending to be a render.
  previewEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    color: colors.white_30,
  },
  previewEmptyIcon: {
    fontSize: 22,
  },
  previewEmptyText: {
    fontSize: '10px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  cardTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: COLORS.WHITE,
  },
  cardSpots: {
    fontSize: '12px',
    color: colors.white_50,
  },
  cardDescription: {
    fontSize: '12px',
    lineHeight: 1.4,
    color: colors.white_40,
  },
  cardWarning: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    color: '#FFB800',
  },
  cardWarningText: {
    fontSize: '11px',
    lineHeight: 1.35,
    color: 'inherit',
  },
  cardWarningSevere: {
    color: '#FF6B6B',
  },
  actions: {
    padding: '8px 20px 16px',
  },
});
